import React, {PureComponent, type Node} from 'react';
import classNames from 'classnames';

import s from './sticky.scss';

type StateType = {
    isActive: boolean,
    elementHeight: number,
};

type PropsType = {
    children: Node,
    enabled: boolean,
    offset: number,
    isForceStuck: boolean,
};

/**
 * Sticky Component
 * Notice that this component is only used on header
 * If you want to use this component in some other part of the application be sure that it fits
 * your requirements. Also, in case you would rewrite something here, please make sure it doesn't
 * break header.
 */
export class Sticky extends PureComponent<PropsType, StateType> {
    static defaultProps = {
        offset: 0,
        enabled: true,
        isForceStuck: false,
    };

    state = {
        isActive: false,
        elementHeight: 0,
    };

    /**
     * Called after component was mounted for the first time
     */
    componentDidMount() {
        if (this.props.enabled) {
            this.onSticky();
            window.addEventListener('scroll', this.onSticky);
        }
    }

    /**
     * For enable or disable sticky header for category page
     * @param {PropsType} prevProps - previously component properties
     */
    componentDidUpdate(prevProps: PropsType) {
        if (prevProps.enabled !== this.props.enabled) {
            if (this.props.enabled) {
                this.onSticky();
                window.addEventListener('scroll', this.onSticky);
            } else {
                window.removeEventListener('scroll', this.onSticky);
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({isActive: false});
            }
        }
    }

    /**
     * Method is invoked immediately before a component is unmounted and destroyed.
     */
    componentWillUnmount() {
        window.removeEventListener('scroll', this.onSticky);
    }

    /**
     * On Sticky
     */
    onSticky = () => {
        if (this.props.isForceStuck) {
            return;
        }

        const {isActive, elementHeight} = this.state;
        const scrollTop = document.documentElement ? document.documentElement.scrollTop : 0;
        const winTop = (window.pageYOffset || scrollTop) + (isActive ? elementHeight : 0);

        if (
            winTop >
            this.element.clientHeight + this.element.getBoundingClientRect().top + this.props.offset
        ) {
            this.setState({
                isActive: true,
                elementHeight: this.element.getBoundingClientRect().height,
            });
        } else {
            this.setState({isActive: false, elementHeight: 0});
        }
    };

    /**
     * Renders the component
     * @return {Node} rendered component
     */
    render(): Node {
        const {children, isForceStuck} = this.props;
        const {isActive, elementHeight} = this.state;
        const isStuck = isActive || isForceStuck;
        return (
            <>
                <div
                    className={classNames(s.placeholder, {[s.isActive]: isStuck})}
                    style={{height: `${elementHeight}px`}}
                />
                <div
                    className={classNames({[s.stuck]: isStuck})}
                    ref={item => (this.element = item)}
                >
                    {children}
                </div>
            </>
        );
    }
}
